import type { HouseholdType, Projection } from '../calc/types';
import type { CountryProfile } from './types';
import type { FxRates } from './fx';
import { convert, convertToBase } from './fx';
import { bandOf, scoreOf, type Band } from './score';

/**
 * Two people living together don't spend twice what one person does — rent,
 * utilities and a host of other costs are shared rather than duplicated. 1.7x
 * a single person's budget is a common rule-of-thumb approximation for this.
 */
export const COUPLE_COST_OF_LIVING_MULTIPLIER = 1.7;

/**
 * A single retiree's "comfortable" annual budget in GBP, staying in the UK —
 * the PLSA Retirement Living Standards figure (2022 edition, outside-London),
 * as reported by Hargreaves Lansdown. This is the one hand-authored number in
 * the whole cost-of-living calculation; every country's figure, including the
 * UK's own, is derived from it via `comfortableSingleLocal` below rather than
 * typed in separately.
 * Source: https://www.hl.co.uk/pensions/insights/the-cost-of-a-comfortable-retirement
 */
export const COMFORTABLE_SINGLE_ANNUAL_GBP = 37_300;

/**
 * The UK's own Numbeo Cost of Living Index — the anchor every other
 * country's index is rebased against, since the index is a ratio scale (not
 * an absolute currency figure) and needs a fixed point to convert from.
 * Source: https://www.numbeo.com/cost-of-living/rankings_by_country.jsp
 */
export const UK_COST_OF_LIVING_INDEX = 68.2;

/**
 * A comfortable single retiree's annual budget in `country`'s own currency,
 * derived rather than stored: rebase the UK's cited comfortable-retirement
 * figure by the ratio of Numbeo indices, then convert GBP -> home currency ->
 * country currency through the same live FX rates used for income elsewhere
 * in this file.
 */
export function comfortableSingleLocal(country: CountryProfile, rates: FxRates): number {
  const comfortableGBP =
    (country.factors.costOfLiving.costOfLivingIndex / UK_COST_OF_LIVING_INDEX) * COMFORTABLE_SINGLE_ANNUAL_GBP;
  return convert(convertToBase(comfortableGBP, 'GBP', rates), country.currency, rates);
}

export interface CountryComparison {
  country: CountryProfile;
  /** 0–100 across tax/visa, healthcare, property rights, climate and pension uprating. */
  score: number;
  band: Band;
  homeCurrency: string;
  /** Retirement income in the user's own currency, today's purchasing power. */
  incomeRealHome: number;
  /** Same, converted into the country's currency. */
  incomeRealLocal: number;
  /** Nominal (future money) income grossed up by the app's single global inflation rate — a rough approximation, not a per-country forecast. */
  incomeNominalApproxHome: number;
  incomeNominalApproxLocal: number;
  /** A single retiree's comfortable budget, before the couple multiplier below is applied. */
  costOfLivingSingleLocal: number;
  costOfLivingLocal: number;
  /** incomeRealLocal - costOfLivingLocal. Positive means the projected income covers the budget. */
  surplusReal: number;
  /**
   * The same surplus in the user's own currency. Countries can only be ranked
   * or compared on this one — `surplusReal` is quoted in each country's own
   * money, so a yen figure and a euro figure are not the same kind of number.
   */
  surplusRealHome: number;
  surplusNominalApprox: number;
}

export function compareCountry(
  country: CountryProfile,
  projection: Projection,
  rates: FxRates,
  householdType: HouseholdType,
): CountryComparison {
  const incomeRealLocal = convert(projection.incomeReal, country.currency, rates);
  const incomeNominalApproxLocal = convert(projection.incomeNominal, country.currency, rates);
  const costOfLivingSingleLocal = comfortableSingleLocal(country, rates);
  const costOfLivingLocal =
    householdType === 'couple' ? costOfLivingSingleLocal * COUPLE_COST_OF_LIVING_MULTIPLIER : costOfLivingSingleLocal;

  const score = scoreOf(country);

  return {
    country,
    score,
    band: bandOf(score),
    homeCurrency: rates.base,
    incomeRealHome: projection.incomeReal,
    incomeRealLocal,
    incomeNominalApproxHome: projection.incomeNominal,
    incomeNominalApproxLocal,
    costOfLivingSingleLocal,
    costOfLivingLocal,
    surplusReal: incomeRealLocal - costOfLivingLocal,
    surplusRealHome: projection.incomeReal - convertToBase(costOfLivingLocal, country.currency, rates),
    surplusNominalApprox: incomeNominalApproxLocal - costOfLivingLocal,
  };
}

export function compareAll(
  countries: readonly CountryProfile[],
  projection: Projection,
  rates: FxRates,
  householdType: HouseholdType,
): CountryComparison[] {
  return countries.map((c) => compareCountry(c, projection, rates, householdType));
}
